import { useReducer, useRef } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { reportingLeDataSessionPath } from '~/consts/reportingLeDataApi';

import { request } from '~/services/common';

import { getLeDataApiErrorMessage } from '~/utils/leData/getLeDataApiErrorMessage';
import { resolveReportingLeDataUrl } from '~/utils/resolveReportingLeDataUrl';

import { UI_MESSAGE_STREAM_WIRE } from './askTransport';
import { type SessionHistory, sessionHistoryFromBody } from './sessionHistory';

const AWAIT_REPLY_POLL_MS = 2000;
const SVC_TURN_TIMEOUT_MS = 600_000;
const AWAIT_REPLY_POLL_LIMIT = Math.ceil(SVC_TURN_TIMEOUT_MS / AWAIT_REPLY_POLL_MS) + 30;
const AWAIT_REPLY_ERROR_LIMIT = 3;

interface HistoryPollState {
  data?: SessionHistory;
  dataUpdateCount: number;
  errorUpdateCount: number;
}

interface HistoryPollBudget {
  sessionId: string;
  turnId: string;
  startDataUpdateCount: number;
  lastDataUpdateCount: number;
  errorUpdateCount: number;
}

export const uiStreamSessionQueryKeys = {
  all: ['reporting', 'le-data', 'session', UI_MESSAGE_STREAM_WIRE] as const,
  detail: (sessionId: string) => [...uiStreamSessionQueryKeys.all, sessionId] as const,
};

export function useUiStreamSessionHistory(sessionId: string | null) {
  const queryClient = useQueryClient();
  const pollBudget = useRef<HistoryPollBudget | null>(null);
  const [, resumePolling] = useReducer((revision: number) => revision + 1, 0);
  const queryKey = uiStreamSessionQueryKeys.detail(sessionId ?? '');
  const history = useQuery<SessionHistory>({
    queryKey,
    queryFn: async ({ signal }) => {
      if (!sessionId) {
        throw new Error('Session ID is required');
      }
      try {
        const url = `${resolveReportingLeDataUrl(reportingLeDataSessionPath(sessionId))}?wire=${UI_MESSAGE_STREAM_WIRE}`;
        const body = await request(url, { signal });
        return sessionHistoryFromBody(body, sessionId);
      } catch (error) {
        throw new Error(getLeDataApiErrorMessage(error), { cause: error });
      }
    },
    enabled: Boolean(sessionId),
    refetchInterval: (query) => (historyIsAwaitingReply(query.state, pollBudget) ? AWAIT_REPLY_POLL_MS : false),
    staleTime: 0,
    gcTime: 0,
  });
  const awaitingReply = historyIsAwaitingReply(queryClient.getQueryState<SessionHistory>(queryKey), pollBudget);
  return {
    ...history,
    refetch: async (options?: Parameters<typeof history.refetch>[0]) => {
      const result = await history.refetch(options);
      if (result.isSuccess) {
        pollBudget.current = null;
        resumePolling();
      }
      return result;
    },
    awaitingReply,
    expired: historyAwaitsReply(history.data) && !awaitingReply,
  };
}

export function historyIsAwaitingReply(
  state: HistoryPollState | undefined,
  budget: { current: HistoryPollBudget | null },
): boolean {
  if (!state?.data || !historyAwaitsReply(state.data)) {
    budget.current = null;
    return false;
  }
  const { data, dataUpdateCount, errorUpdateCount } = state;
  const turnId =
    [...data.messages].reverse().find((message) => message.role === 'user')?.id ??
    data.messages[data.messages.length - 1].id;
  if (budget.current?.sessionId !== data.sessionId || budget.current?.turnId !== turnId) {
    budget.current = {
      sessionId: data.sessionId,
      turnId,
      startDataUpdateCount: dataUpdateCount - 1,
      lastDataUpdateCount: dataUpdateCount,
      errorUpdateCount,
    };
  }
  if (budget.current.lastDataUpdateCount !== dataUpdateCount) {
    budget.current.lastDataUpdateCount = dataUpdateCount;
    budget.current.errorUpdateCount = errorUpdateCount;
  }
  return (
    dataUpdateCount - budget.current.startDataUpdateCount < AWAIT_REPLY_POLL_LIMIT &&
    errorUpdateCount - budget.current.errorUpdateCount < AWAIT_REPLY_ERROR_LIMIT
  );
}

export function historyAwaitsReply(history: SessionHistory | undefined): boolean {
  if (!history || history.readOnly) {
    return false;
  }
  const last = history.messages[history.messages.length - 1];
  return (
    last?.role === 'user' || (last?.role === 'assistant' && !last.metadata?.createdAt && !last.metadata?.errorText)
  );
}
